/* eslint-disable sort-exports/sort-exports */
import React from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import OrderStatusPill from 'components/OrderStatusPill';
import { isStringEnumKey } from 'services';
import { OrderStatus } from 'global-constants';
import { getShipmentInfo } from 'pages/Shipping/utils';
import * as OrderDetailsPanelStyles from './styles';
import { OrderDetailsPanelProps } from './types';
import PinsAndKeys from './components/PinsAndKeys';
import { procurementPreference } from './utils/utils';
import { showProcurementPreferencesSection } from 'shared/utils';

const OrderDetailsPanel = (props: OrderDetailsPanelProps) => {
  const styles = OrderDetailsPanelStyles;

  const formatAddressString = (addressString: string) => {
    const multiLineText = addressString
      .split('\n')
      .map((addressLine, index) => (
        <React.Fragment key={index}>
          {addressLine}
          {index < addressString.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));

    return <Typography variant="body1">{multiLineText}</Typography>;
  };

  const getOrderStatusPill = (status: string) =>
    isStringEnumKey(status, OrderStatus) ? (
      <OrderStatusPill
        showAdditionalText
        clickEvent={props.clickEvent}
        status={status as OrderStatus}
      />
    ) : (
      <Typography variant="overline" color="colors.gray60">
        {status}
      </Typography>
    );

  const isProcessType = props.orderType === 'Process';

  return (
    <Grid item container direction="column" sx={{ width: '100%' }}>
      <Typography variant="subtitle2">Allwhere info</Typography>
      <Stack sx={styles.OrderDetailsInfoStackSx} direction="row">
        <Grid container sx={styles.OrderDetailsInfoGridSx}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              ORDER NUMBER
            </Typography>
            <Typography variant="body1">{props.orderNumber}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              ORDER TYPE
            </Typography>
            <Typography variant="body1">{props.orderType}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              ORDER DATE
            </Typography>
            <Typography variant="body1">{props.orderDate}</Typography>
          </Grid>
          {/* Commenting out for now as it isn't consistent with getPath v2 and customers don't really need it */}
          {/* <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              SOURCE
            </Typography>
            <Typography variant="body1">{props.source}</Typography>
          </Grid> */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              SHIPPING
            </Typography>
            <Typography variant="body1">{props.shipping}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="overline" color="colors.gray60">
                ESTIMATED DELIVERY
              </Typography>
              <Tooltip
                title={
                  <Typography variant="body2">
                    To learn more about delivery estimates please visit a{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      onClick={() =>
                        window.open(
                          'https://faq.allwhere.co/en_us/categories/faq-S1PKSbsci',
                          '_blank'
                        )
                      }
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        display: 'inline',
                      }}
                    >
                      FAQ
                    </Typography>{' '}
                    page
                  </Typography>
                }
                arrow
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '16px',
                    borderRadius: '50%',
                    color: 'rgba(19, 20, 20, 0.54)',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Stack>
            <Typography
              variant="body1"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {(() => {
                const orderItems = props.orderItems || [];
                let hasTrackingNumbers = false;

                // Get all items with delivery dates and sort them
                const itemsWithDates = orderItems
                  .filter((item: any) => {
                    const shipmentInfo = getShipmentInfo(item);
                    if (shipmentInfo.trackingNumber) {
                      hasTrackingNumbers = true;
                    }
                    return shipmentInfo.expectedDeliveryDate;
                  })
                  .sort((a: any, b: any) => {
                    const dateA = new Date(
                      getShipmentInfo(a).expectedDeliveryDate || ''
                    );
                    const dateB = new Date(
                      getShipmentInfo(b).expectedDeliveryDate || ''
                    );
                    return dateA.getTime() - dateB.getTime();
                  });

                if (itemsWithDates.length === 0) {
                  return hasTrackingNumbers ? 'Pending' : '-';
                }

                const earliestDate = moment.utc(
                  getShipmentInfo(itemsWithDates[0]).expectedDeliveryDate
                ).format('MMM D');
                const additionalItems = itemsWithDates.length - 1;

                let more = null;
                if (additionalItems > 0) {
                  more = (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: 'colors.gray200',
                        color: 'colors.black',
                        fontSize: '12px',
                        '&:hover': {
                          bgcolor: 'colors.gray200',
                        },
                      }}
                    >
                      +{additionalItems}
                    </Avatar>
                  );
                }

                return (
                  <>
                    {earliestDate}
                    {more}
                  </>
                );
              })()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              ORDER STATUS
            </Typography>
            <Typography variant="body1">
              {getOrderStatusPill(props.orderStatus)}
            </Typography>
          </Grid>
        </Grid>
      </Stack>

      {isProcessType ? <PinsAndKeys {...props} /> : null}

      <Typography variant="subtitle2" pt="20px" pb="10px">
        Recipient info
      </Typography>
      <Stack sx={styles.OrderDetailsInfoStackSx} direction="row">
        <Grid container sx={styles.OrderDetailsInfoGridSx}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              RECIPIENT
            </Typography>
            <Typography variant="body1">{props.recipient}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              CONTACT EMAIL
            </Typography>
            <Typography variant="body1">{props.recipientEmail}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {/** empty 3rd block to wrap next blocks on a new row */}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              RECIPIENT ADDRESS
            </Typography>
            <Typography variant="body1">
              {formatAddressString(props.recipientAddress)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="overline" color="colors.gray60">
              FINAL DESTINATION
            </Typography>
            <Typography variant="body1">
              {formatAddressString(props.finalDestination || 'Pending')}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
      {showProcurementPreferencesSection(
        props.deviceColorSwapApproved,
        props.flexibleLeadTimeApproved
      ) && (
        <>
          <Typography variant="subtitle2" pt="20px" pb="10px">
            Preferences
          </Typography>
          <Stack sx={styles.OrderDetailsInfoStackSx} direction="row">
            <Grid container sx={styles.OrderDetailsInfoGridSx}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="overline" color="colors.gray60">
                  COLOR SWAP
                </Typography>
                {procurementPreference(props.deviceColorSwapApproved ?? false)}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="overline" color="colors.gray60">
                  LEAD TIME
                </Typography>
                {procurementPreference(props.flexibleLeadTimeApproved ?? false)}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {/** empty 3rd block to wrap next blocks on a new row */}
              </Grid>
            </Grid>
          </Stack>
        </>
      )}
    </Grid>
  );
};

export default OrderDetailsPanel;
